import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

/*
 * LatencyProbe — measures the browser's round-trip time to our origin
 * once per session (5 samples, take min). The baseline is a signal for
 * "how far am I network-wise". Combined with a per-region offset table
 * it yields a user-adjusted ping estimate.
 */

const SESSION_KEY = "wd.probe.baseline";
const SESSION_SAMPLES = "wd.probe.samples";

const LatencyCtx = createContext(null);

// Approximate ping (ms) from Emergent's edge (assumed EU/US-adjacent)
// to each major region. These are the offsets we add to the baseline.
const REGION_OFFSET = {
  "US-East": 15, "US-West": 55, "NA-East": 15, "NA-West": 55,
  "NA-Central": 35, "NA-North": 40,
  "EU-Central": 10, "EU-West": 25, "EU-East": 45, "EU-South": 40, "Europe": 15,
  "Asia-East": 150, "Asia-West": 120, "Asia-SE": 170, "Asia": 150,
  "Oceania": 220, "OCE-Sydney": 220,
  "SA-Brazil": 180, "south-america": 180, "Middle East": 160, "Africa": 190,
  "Unknown": 60, "Unspecified": 60, "World": 60,
};

const measureOnce = async () => {
  const start = performance.now();
  await api.get("/ping", { headers: { "Cache-Control": "no-cache" } });
  return performance.now() - start;
};

const measureBaseline = async (samples = 5) => {
  // warm-up
  try { await measureOnce(); } catch { return null; }
  const times = [];
  for (let i = 0; i < samples; i++) {
    try {
      const ms = await measureOnce();
      times.push(ms);
    } catch { /* ignore */ }
  }
  if (!times.length) return null;
  // Use the p25 (robust to spikes)
  times.sort((a, b) => a - b);
  const pick = times[Math.floor(times.length * 0.25)];
  return Math.max(1, Math.round(pick));
};

export const LatencyProbeProvider = ({ children }) => {
  const [baseline, setBaseline] = useState(() => {
    if (typeof window === "undefined") return null;
    const stored = sessionStorage.getItem(SESSION_KEY);
    return stored ? Number(stored) : null;
  });
  const [samples, setSamples] = useState(() => {
    if (typeof window === "undefined") return null;
    const stored = sessionStorage.getItem(SESSION_SAMPLES);
    return stored ? Number(stored) : null;
  });
  const [enabled, setEnabled] = useState(() => {
    if (typeof window === "undefined") return false;
    // Default = SERVER-REPORTED. Existing users who had explicitly chosen
    // HOME ('1') keep that preference; everyone else starts on SERVER.
    return localStorage.getItem("wd.probe.enabled") === "1";
  });

  useEffect(() => {
    // Measure baseline regardless of the `enabled` flag so the dual tooltip
    // can always surface a truthful "from home" value even while the user is
    // viewing SERVER mode. Toggling the mode is purely a UI concern.
    if (baseline != null) return;
    (async () => {
      const b = await measureBaseline(5);
      if (b != null) {
        setBaseline(b);
        setSamples(5);
        sessionStorage.setItem(SESSION_KEY, String(b));
        sessionStorage.setItem(SESSION_SAMPLES, "5");
      }
    })();
  }, [baseline]);

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem("wd.probe.enabled", next ? "1" : "0");
  };

  const remeasure = async () => {
    setBaseline(null);
    sessionStorage.removeItem(SESSION_KEY);
    const b = await measureBaseline(5);
    if (b != null) {
      setBaseline(b);
      setSamples(5);
      sessionStorage.setItem(SESSION_KEY, String(b));
      sessionStorage.setItem(SESSION_SAMPLES, "5");
    }
  };

  // Pure helper: compute what the home-measured ping to this server WOULD be,
  // independent of whether the user has HOME mode active. Used by tooltips
  // to always show both numbers side-by-side.
  const computeHomePing = (server) => {
    if (baseline == null || !server) return null;
    const offset = REGION_OFFSET[server.region] ?? REGION_OFFSET[server.region?.split("-")[0]] ?? 60;
    return Math.max(5, Math.round(baseline + offset));
  };

  /*
   * Translate a server's region label into an adjusted ping using the probe.
   * Formula: effective = baseline + REGION_OFFSET[region]
   * (The baseline already reflects the user's distance to our origin,
   * and the offset adds the extra geographic hop to the target region.)
   */
  const adjustedPing = (server) => {
    if (!enabled || baseline == null || !server) return server?.ping ?? 0;
    return computeHomePing(server) ?? server.ping ?? 0;
  };

  return (
    <LatencyCtx.Provider value={{ baseline, samples, enabled, toggle, remeasure, adjustedPing, computeHomePing }}>
      {children}
    </LatencyCtx.Provider>
  );
};

export const useLatency = () => {
  const ctx = useContext(LatencyCtx);
  if (!ctx) throw new Error("useLatency must be used inside LatencyProbeProvider");
  return ctx;
};
